import { IconCopyOutline18 as Copy } from "nucleo-ui-outline-18"

import type { ResolvedToolPolicyVariant } from "@productlane/lib"
import {
	MAX_VARIANT_DESCRIPTION_CHARS,
	MAX_VARIANT_LABEL_CHARS,
} from "@productlane/lib"
import { Button, Input, Label } from "@productlane/ui"

import type { VariantPatch } from "./use-policy-draft"
import { useCommittedDraft } from "../inputs/use-committed-draft"

interface Props {
	variant: ResolvedToolPolicyVariant
	isBase: boolean
	toolName: string
	onChange: (patch: VariantPatch, options?: { save?: boolean }) => void
	onDuplicate: () => void
}

// The label and description are what the agent reads to pick between two
// configurations of the same tool, so they are edited right above the rules.
export function VariantHeader({
	variant,
	isBase,
	toolName,
	onChange,
	onDuplicate,
}: Props) {
	const id = variant.id || "base"

	const label = useCommittedDraft<string | undefined>(
		// The base variant falls back to the tool name, which is a placeholder here
		// rather than something the author typed.
		isBase && variant.label === toolName ? "" : variant.label,
		{
			format: (value) => value ?? "",
			// A named configuration has to keep a name, so an emptied one reverts.
			parse: (text) => {
				const trimmed = text.trim()
				if (!isBase && !trimmed) return { ok: false }
				return { ok: true, value: trimmed || undefined }
			},
			onCommit: (value) => onChange({ label: value }),
		},
	)

	const description = useCommittedDraft<string | undefined>(
		variant.description,
		{
			format: (value) => value ?? "",
			parse: (text) => ({ ok: true, value: text.trim() || undefined }),
			onCommit: (value) => onChange({ description: value }),
		},
	)

	return (
		<div className="grid gap-2 rounded-lg bg-gray-100 p-2.5 dark:bg-gray-800">
			<div className="grid gap-1.5">
				<div className="flex items-center justify-between gap-2">
					<Label htmlFor={`variant-label-${id}`}>Name</Label>
					<Button
						variant="ghost"
						Icon={Copy}
						onClick={onDuplicate}
						data-testid="policy-variant-duplicate"
					>
						Duplicate
					</Button>
				</div>
				<Input
					name={`variant-label-${id}`}
					value={label.value}
					placeholder={isBase ? toolName : "Invoices"}
					maxLength={MAX_VARIANT_LABEL_CHARS}
					onChange={(event) => label.setValue(event.target.value)}
					onBlur={label.commit}
				/>
			</div>
			<div className="grid gap-1.5">
				<Label htmlFor={`variant-description-${id}`}>
					What the agent uses this for
				</Label>
				<Input
					name={`variant-description-${id}`}
					value={description.value}
					placeholder="Read invoices for the customer in this conversation"
					maxLength={MAX_VARIANT_DESCRIPTION_CHARS}
					onChange={(event) => description.setValue(event.target.value)}
					onBlur={description.commit}
				/>
			</div>
		</div>
	)
}
